/**
 * API Service Module
 * 
 * This module provides all API communication functions for the Bank Automation
 * Admin Dashboard. It creates a configured Axios instance with JWT authentication
 * and defines endpoints for banks, keys, projects, and authentication.
 * 
 * All requests automatically include the JWT token from session storage
 * in the x-api-key header.
 */
import axios from 'axios';

// Base URL for the backend API
// Use environment variable for API URL, with a fallback to the production URL
const API_URL = process.env.REACT_APP_API_URL || 'https://autobankapi.z777.in/api';

/**
 * Configured Axios instance with default headers
 * All requests use JSON content type
 */
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * Request interceptor to add JWT authentication token
 * Retrieves token from sessionStorage and adds it to x-api-key header
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('jwtToken');
    if (token) {
      config.headers['x-api-key'] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor to handle authentication failures
 * Redirects to /login and clears sessionStorage on 401 Unauthorized
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response ? error.response.status : null;
    const detail = error.response?.data?.detail;
    const isTokenExpired =
      status === 401 ||
      status === 403 ||
      (typeof detail === 'string' &&
        (detail.toLowerCase().includes('token expired') ||
          detail.toLowerCase().includes('invalid token') ||
          detail.toLowerCase().includes('authorization header missing')));

    if (isTokenExpired) {
      sessionStorage.removeItem('jwtToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// Banks API - Bank account management endpoints
// ============================================================================

/**
 * Fetches the list of all bank accounts
 * @returns {Promise} List of bank accounts with their status and configuration
 */
export const getBankList = (params) => axiosInstance.get('/bank/list', { params });

/**
 * Fetches details for a specific bank account
 * @param {number} bankId - The ID of the bank account
 * @returns {Promise} Bank account details including credentials and settings
 */
export const getBank = (bankId) => axiosInstance.get(`/bank?bank_id=${bankId}`);

/**
 * Fetches logs for a specific bank account
 * @param {number} bankId - The ID of the bank account
 * @returns {Promise} Array of log entries with timestamps and messages
 */
export const getBankLogs = (bankId) => axiosInstance.get(`/bank/logs?bank_id=${bankId}`);

/**
 * Creates a new bank account
 * @param {Object} data - Bank account data including credentials, project_id, key_id
 * @returns {Promise} Created bank account details
 */
export const createBank = (data) => axiosInstance.post('/bank/create', data);

/**
 * Deletes a bank account
 * @param {number} bankId - The ID of the bank account to delete
 * @returns {Promise} Deletion confirmation
 */
export const deleteBank = (bankId) =>
  axiosInstance.delete(`/bank/delete?bank_id=${bankId}`);

/**
 * Updates an existing bank account
 * @param {number} bankId - The ID of the bank account
 * @param {Object} data - Updated bank account data
 * @returns {Promise} Updated bank account details
 */
export const updateBank = (bankId, data) =>
  axiosInstance.put(`/bank/update?bank_id=${bankId}`, data, {
    headers: { 'Content-Type': 'application/json' },
  });

/**
 * Fetches the list of available bank names (e.g., sbi, hdfc, icici)
 * @returns {Promise} Array of supported bank name strings
 */
export const getBankNames = () => axiosInstance.get('/bank/names');

/**
 * Fetches the list of supported countries
 * @returns {Promise} Array of country codes (IND, BGD, LKA)
 */
export const getBankCountrys = () => axiosInstance.get('/bank/countrys');

/**
 * Turns on a bank account (starts the automation worker)
 * Creates a task in Redis queue that will be picked up by APLA module
 * @param {number} accountId - The ID of the bank account to activate
 * @returns {Promise} Activation confirmation
 */
export const switchBankOn = (accountId) =>
  axiosInstance.put(`/bank/panel/switch_on`, null, {
    params: { account_id: accountId },
  });

/**
 * Turns off a bank account (stops the automation worker)
 * Sends stop signal to the running task via Redis
 * @param {number} accountId - The ID of the bank account to deactivate
 * @returns {Promise} Deactivation confirmation
 */
export const switchBankOff = (accountId) =>
  axiosInstance.put(`/bank/panel/switch_off`, null, {
    params: { account_id: accountId },
  });

/**
 * Turns on SMS automation for a bank account.
 * @param {number} accountId - The ID of the bank account
 * @returns {Promise} Confirmation
 */
export const switchSmsOn = (accountId) =>
  axiosInstance.put(`/bank/panel/switch_sms_on`, null, {
    params: { account_id: accountId },
  });

/**
 * Turns off SMS automation for a bank account.
 * @param {number} accountId - The ID of the bank account
 * @returns {Promise} Confirmation
 */
export const switchSmsOff = (accountId) =>
  axiosInstance.put(`/bank/panel/switch_sms_off`, null, {
    params: { account_id: accountId },
  });

// ============================================================================
// Keys API - Parser key management endpoints
// ============================================================================

/**
 * Fetches keys filtered by bank name
 * @param {string} bankName - The bank name to filter keys by
 * @returns {Promise} Array of parser keys for the specified bank
 */
export const getKeyList = (bankName) =>
  axiosInstance.get('/utils/bank_key/account_name/list', {
    params: { bank_name: bankName },
  });

/**
 * Fetches all parser keys
 * @returns {Promise} Array of all parser keys with obfuscated key values
 */
export const getFullKeyList = () => axiosInstance.get('/utils/bank_key/list');

/**
 * Creates a new parser key
 * @param {Object} data - Key data including bank_name, key value, and type (utr/last_trn/order_id)
 * @returns {Promise} Created key details
 */
export const createKey = (data) => axiosInstance.post('/utils/bank_key/create', data);

/**
 * Deletes a parser key
 * @param {number} keyId - The ID of the key to delete
 * @returns {Promise} Deletion confirmation
 */
export const deleteKey = (keyId) =>
  axiosInstance.delete('/utils/bank_key/delete', { params: { key_id: keyId } });

// ============================================================================
// Projects API - Project management endpoints
// ============================================================================

/**
 * Fetches the list of all projects
 * Projects define callback URLs where transaction data is sent
 * @returns {Promise} Array of project objects with id and name
 */
export const getProjectList = () => axiosInstance.get('/utils/bank_project/list/');

/**
 * Creates a new project
 * @param {Object} data - Project data including name and callback_url
 * @returns {Promise} Created project details
 */
export const createProject = (data) => axiosInstance.post('/utils/bank_project/create', data);

/**
 * Deletes a project
 * @param {number} projectId - The ID of the project to delete
 * @returns {Promise} Deletion confirmation
 */
export const deleteProject = (projectId) =>
  axiosInstance.delete('/utils/bank_project/delete', { params: { project_id: projectId } });

// ============================================================================
// Auth API - Authentication endpoints
// ============================================================================

/**
 * Authenticates a user and returns a JWT token
 * @param {Object} data - Login credentials (login, password)
 * @returns {Promise} Object containing JWT token
 */
export const login = (data) => axiosInstance.post('/auth', data);

/**
 * Decodes and retrieves the current user's role from the stored JWT token
 * @returns {string|null} The role string (e.g. 'ADMIN', 'AXIS_OPERATOR', 'USER') or null
 */
export const getUserRole = () => {
  try {
    const token = sessionStorage.getItem('jwtToken');
    if (!token) return null;
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const parsed = JSON.parse(jsonPayload);
    return parsed.role || 'USER';
  } catch (e) {
    return null;
  }
};

/**
 * Checks whether the stored JWT token exists and has not expired
 * @returns {boolean} True if token exists and is still valid
 */
export const isTokenValid = () => {
  try {
    const token = sessionStorage.getItem('jwtToken');
    if (!token) return false;
    const base64Url = token.split('.')[1];
    if (!base64Url) return false;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const parsed = JSON.parse(jsonPayload);
    if (parsed.exp && parsed.exp * 1000 <= Date.now()) {
      sessionStorage.removeItem('jwtToken');
      return false;
    }
    return true;
  } catch (e) {
    sessionStorage.removeItem('jwtToken');
    return false;
  }
};

// ============================================================================
// Batch Payouts API
// ============================================================================

export const getBatchList = (params) => axiosInstance.get('/batch/list', { params });

export const uploadBatch = (formData) => axiosInstance.post('/batch/upload', formData, {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

export const confirmBatch = (data) => axiosInstance.post('/batch/confirm', data);

export const getBatchTransactions = (batchId, params) =>
  axiosInstance.get(`/batch/${batchId}/transactions`, { params });

export const getBatchDetails = (batchId, params) => axiosInstance.get(`/batch/${batchId}`, { params });

export const reprocessBatch = (batchId) => axiosInstance.post(`/batch/${batchId}/reprocess`);

export const exportBatchSuccess = (batchId) =>
  axiosInstance.get(`/batch/${batchId}/export/success`, { responseType: 'blob' });

export const exportBatchFailure = (batchId) =>
  axiosInstance.get(`/batch/${batchId}/export/failure`, { responseType: 'blob' });

export const exportBatchFull = (batchId) =>
  axiosInstance.get(`/batch/${batchId}/export/full`, { responseType: 'blob' });

export const startBatch = (batchId) => axiosInstance.post(`/batch/${batchId}/start`);
export const stopBatch = (batchId) => axiosInstance.post(`/batch/${batchId}/stop`);

export const getBatchStatus = () => axiosInstance.get('/batch/status');
// Batch Payout Toggle ON/OFF POST /api/batch/status (Body: { "enabled": true })
export const toggleBatchStatus = (data) => axiosInstance.post('/batch/status', data);

// ============================================================================
// Fund Management API
// ============================================================================

export const getFundRules = (params) => axiosInstance.get('/fund/rules', { params });

export const createFundRule = (data) => axiosInstance.post('/fund/rules', data);

export const updateFundRule = (id, data) => axiosInstance.put(`/fund/rules/${id}`, data);

export const deleteFundRule = (id) => axiosInstance.delete(`/fund/rules/${id}`);

export const getFundMonitor = () => axiosInstance.get('/fund/monitor');

export const refreshBankBalance = (bankId) => axiosInstance.post(`/bank/refresh?bank_id=${bankId}`);

export const getTransferLogs = (params) => axiosInstance.get('/fund/logs', { params });

export const getFundStatus = () => axiosInstance.get('/fund/status');
export const toggleFundStatus = (data) => axiosInstance.post('/fund/status', data);

// ============================================================================
// Manual Transfers API
// ============================================================================

export const initiateTransfer = (data) => axiosInstance.post('/transfers/initiate', data);

export const approveTransfer = (data) => axiosInstance.post('/transfers/approve', data);

// ============================================================================
// Bot Control API
// ============================================================================

export const getBotStatus = () => axiosInstance.get('/bot/status');

export const startBot = () => axiosInstance.post('/bot/start');

export const stopBot = () => axiosInstance.post('/bot/stop');

export const forceRunBot = () => axiosInstance.post('/bot/force-run');

export const updateSchedulerConfig = (data) => axiosInstance.put('/scheduler/config', data);

// ============================================================================
// Automation Log Management API
// ============================================================================

/**
 * Fetches paginated automation logs with filters (log_type, level, dates, search, account)
 * @param {Object} params - Query params { log_type, level, bank_name, account_id, project_id, start_date, end_date, search, page, page_size }
 * @returns {Promise} Paginated logs response with stats
 */
export const getAutomationLogs = (params) => axiosInstance.get('/logs', { params });

/**
 * Fetches list of all distinct log types and their counts
 * @returns {Promise} List of log types
 */
export const getAutomationLogTypes = (params) => axiosInstance.get('/logs/types', { params });

/**
 * Creates a new log entry
 * @param {Object} data - Log entry payload
 * @returns {Promise}
 */
export const createAutomationLog = (data) => axiosInstance.post('/logs/create', data);

/**
 * Clears old logs before a given date
 * @param {Object} params - { before_date }
 * @returns {Promise}
 */
export const clearAutomationLogs = (params) => axiosInstance.delete('/logs/clear', { params });

export default axiosInstance;

